import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "in", "appear", "enter", "exit", "fieldName", "classNames", "onHeightChange"];
import { CSSTransition } from 'react-transition-group';
import { memo, useRef, useLayoutEffect, useState, useEffect } from 'react';
import styled from 'foundations-theming/utils';
import { ENTER_SUB_TRANSITION_TIME_MS, ENTER_TRANSITION_TIME_MS, EXIT_SUB_TRANSITION_TIME_MS, EXIT_TRANSITION_TIME_MS, TRANSITION_FN } from '../constants/animationConstants';
import { jsx as _jsx } from "react/jsx-runtime";
const AnimatedFormRowTarget = styled.div.withConfig({
  displayName: "AnimatedMetadataField__AnimatedFormRowTarget",
  componentId: "sc-19p6h0s-0"
})(["width:100%;padding-inline:", ";overflow:hidden;&.animated-row-enter{opacity:0;height:0;}&.animated-row-enter-active{opacity:1;transition:opacity ", "ms,height ", "ms ", ";height:", "px;}&.animated-row-exit{opacity:1;height:", "px;}&.animated-row-exit-active{opacity:0;height:0;transition:opacity ", "ms,height ", "ms ", ";}&.animated-row-reverse-enter{opacity:0;height:60px;}&.animated-row-reverse-enter-active{opacity:1;transition:opacity ", "ms,height ", "ms ", ";height:", "px;}"], ({
  theme
}) => theme.space['400'], ENTER_SUB_TRANSITION_TIME_MS, ENTER_TRANSITION_TIME_MS, TRANSITION_FN, ({
  height
}) => height, ({
  height
}) => height, EXIT_SUB_TRANSITION_TIME_MS, EXIT_TRANSITION_TIME_MS, TRANSITION_FN, ENTER_SUB_TRANSITION_TIME_MS, ENTER_TRANSITION_TIME_MS, TRANSITION_FN, ({
  height
}) => height);
const RowMeasurer = styled.div.withConfig({
  displayName: "AnimatedMetadataField__RowMeasurer",
  componentId: "sc-19p6h0s-1"
})(["display:flex;width:100%;"]);
const AnimatedMetadataField = _ref => {
  let {
      children,
      in: inProp,
      appear,
      enter,
      exit,
      fieldName,
      classNames = 'animated-row',
      onHeightChange = () => {}
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const onHeightChangeRef = useRef(newHeight => onHeightChange(fieldName, newHeight));
  const measurerRef = useRef(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const newHeight = measurerRef.current ? measurerRef.current.clientHeight : 0;
    setHeight(newHeight);
  }, [children]);
  useEffect(() => {
    const initialOnHeightChange = onHeightChangeRef.current;
    initialOnHeightChange(height);
    return () => initialOnHeightChange(0);
  }, [height]);

  // Because we have to measure the height for the transition
  // to work regardless of the field's height, we need one extra
  // render before marking the row "in". As such, we also want
  // to delay any props that might prevent animation for a single
  // cycle so that they actually affect the cycle where the transition
  // is marked "in"
  const [delayedProps, setDelayedProps] = useState({
    appear,
    enter,
    exit
  });
  useLayoutEffect(() => {
    setDelayedProps({
      appear,
      enter,
      exit
    });
  }, [appear, enter, exit]);
  return /*#__PURE__*/_jsx(CSSTransition, Object.assign({
    classNames: classNames,
    in: inProp && height > 0
  }, delayedProps, rest, {
    timeout: {
      enter: ENTER_TRANSITION_TIME_MS,
      exit: EXIT_TRANSITION_TIME_MS
    },
    children: /*#__PURE__*/_jsx(AnimatedFormRowTarget, {
      height: height,
      children: /*#__PURE__*/_jsx(RowMeasurer, {
        ref: measurerRef,
        children: children
      })
    })
  }));
};
AnimatedMetadataField.displayName = 'AnimatedMetadataField';
export default /*#__PURE__*/memo(AnimatedMetadataField);